/** Convert streaming markdown tokens to safe HTML for dangerouslySetInnerHTML */
export const parseMarkdown = (text) => {
    if (!text) return '';
    let f = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    /*
     * Emphasis, but NOT inside a word.
     *
     * One rule matched * and _ alike anywhere, so a competency id typed into
     * the chat — DQ_IND_02_01 — came back as DQ<em>IND</em>02_01 and showed
     * as DQIND02_01 with the middle italic and the underscores eaten.
     * Markdown does not treat intra-word underscores as emphasis either.
     *
     * Both forms now require a non-word character (or the string edge)
     * outside the marker, which leaves identifiers and snake_case alone.
     */
    f = f.replace(/(^|[^\w*])\*([^*\n]+?)\*(?!\w)/g, '$1<em>$2</em>');
    f = f.replace(/(^|[^\w_])_([^_\n]+?)_(?!\w)/g, '$1<em>$2</em>');
    f = f.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');
    f = f.replace(/`([^`]+)`/g, '<code>$1</code>');
    f = f.replace(/^(\-{3,}|\*{3,})$/gm, '<hr>');
    f = f.replace(/^(#{1,6})\s+(.*?)$/gm, (_, hashes, content) => `<h${hashes.length}>${content}</h${hashes.length}>`);
    f = f.replace(/^>\s+(.*?)$/gm, '<blockquote>$1</blockquote>');
    f = f.replace(/^\d+\.\s+(.*?)$/gm, '<li>$1</li>');
    f = f.replace(/^[\-\*]\s+(.*?)$/gm, '<li>$1</li>');
    f = f.replace(/<li>.*?<\/li>/gs, match => match.includes('</li><li>') ? `<ul>${match}</ul>` : match);
    f = f.replace(/\n/g, '<br>');
    return f;
};

export const stripMarkers = (text) => {
    if (!text) return '';
    return text
        // complete boolean markers (match both underscore and no-underscore variants)
        .replace(/\{\{session_?info_?captured[^}]*\}\}/g, '')
        .replace(/\{\{redirect_?to_?form[^}]*\}\}/g, '')
        .replace(/\{\{program_?build_?initiated[^}]*\}\}/g, '')
        .replace(/\{\{program_?build_?failed[^}]*\}\}/g, '')
        .replace(/\{\{show_?time_?options[^}]*\}\}/g, '')
        .replace(/\{\{show_?age_?options=[^}]*\}\}/g, '')
        .replace(/\{\{insufficient_?credits_?for_?sessions[^}]*\}\}/g, '')
        .replace(/\{\{session_?limit_?exceeded[^}]*\}\}/g, '')
        // complete value markers
        .replace(/\{\{session_?info=[^}]*\}\}/g, '')
        .replace(/\{\{session_?prefill=[^}]*\}\}/g, '')
        .replace(/\{\{confirm_?build_?cost=[^}]*\}\}/g, '')
        .replace(/\{\{detected_?country=[^}]*\}\}/g, '')
        .replace(/\{\{detected_?content_?style=[^}]*\}\}/g, '')
        .replace(/\{\{detected_?subject=[^}]*\}\}/g, '')
        .replace(/\{\{max_?sessions_?per_?program=[^}]*\}\}/g, '')
        .replace(/\{\{detected_?competencies=[^}]*\}\}/g, '')
        .replace(/\{\{detected_?frameworks=[^}]*\}\}/g, '')
        .replace(/\{\{theme=[^}]*\}\}/g, '')
        .replace(/\{\{target_?age=[^}]*\}\}/g, '')
        .replace(/\{\{program_?code=[^}]*\}\}/g, '')
        // partial markers (no closing }}): truncate at the {{
        .replace(/\{\{session_?info_?captured[^}]*/g, '')
        .replace(/\{\{redirect_?to_?form[^}]*/g, '')
        .replace(/\{\{program_?build_?initiated[^}]*/g, '')
        .replace(/\{\{program_?build_?failed[^}]*/g, '')
        .replace(/\{\{show_?time_?options[^}]*/g, '')
        .replace(/\{\{show_?age_?options=[^}]*/g, '')
        .replace(/\{\{insufficient_?credits_?for_?sessions[^}]*/g, '')
        .replace(/\{\{session_?limit_?exceeded[^}]*/g, '')
        .replace(/\{\{session_?info=[^}]*/g, '')
        .replace(/\{\{session_?prefill=[^}]*/g, '')
        .replace(/\{\{confirm_?build_?cost=[^}]*/g, '')
        .replace(/\{\{detected_?country=[^}]*/g, '')
        .replace(/\{\{detected_?content_?style=[^}]*/g, '')
        .replace(/\{\{detected_?subject=[^}]*/g, '')
        .replace(/\{\{max_?sessions_?per_?program=[^}]*/g, '')
        .replace(/\{\{detected_?competencies=[^}]*/g, '')
        .replace(/\{\{detected_?frameworks=[^}]*/g, '')
        .replace(/\{\{theme=[^}]*/g, '')
        .replace(/\{\{target_?age=[^}]*/g, '')
        .replace(/\{\{program_?code=[^}]*/g, '')
        .trim();
};

/**
 * Extract the value from a `{{key=value}}` marker in text.
 * Returns the raw (URL-encoded) value string, or null if absent / incomplete.
 */
export const extractMarker = (text, marker) => {
    const start = text.indexOf(marker);
    if (start === -1) return null;
    const valStart = start + marker.length;
    const valEnd   = text.indexOf('}}', valStart);
    if (valEnd === -1) return null;
    return text.substring(valStart, valEnd);
};

/**
 * Parse session info clues from the curation prompt's plain-text response.
 * Age is NOT extracted here — it comes exclusively from the {{target_age=}} server marker.
 */
export const extractWizardFromText = (text) => {
    const extras = {};

    // Session duration: "30 minutes", "45 min", "1 hour"
    const durMatch = text.match(/(\d+)\s*(?:minutes?|mins?)\s*(?:per\s*session|each|a\s*session)?/i)
        || text.match(/(\d+)\s*hours?\s*(?:per\s*session|each|a\s*session)/i);
    if (durMatch) {
        const val = parseInt(durMatch[1], 10);
        extras.SessionDuration = text.match(/hour/i) ? val * 60 : val;
        extras.SuggestApproxHours = extras.SessionDuration;
    }

    // Number of sessions / weeks: "6 sessions", "8 weeks", "4 classes"
    const sessMatch = text.match(/(\d+)\s*(?:sessions?|classes?|weeks?|lessons?)/i);
    if (sessMatch) {
        const n = parseInt(sessMatch[1], 10);
        extras.NumSessions = n;
        if (extras.SessionDuration) {
            extras.SuggestApproxHours = n * extras.SessionDuration;
        }
    }


    return extras;
};

/**
 * Rebuild curation-gate refs from the wizard + chat data when `curationState`
 * was not persisted (server only keeps chatHistory and wizard).
 */
export const rebuildCurationState = (chat, wizard) => {
    const state = {
        capturedCompetencies: null,
        capturedFrameworks: null,
        capturedSessionInfo: null,
        capturedAge: null,
        capturedTitle: null,
        sessionInfoCaptured: false,
        buildTriggered: false,
    };
    // If progress messages exist the build was already triggered. Also match
    // the "Creating your program…" marker directly — without it, a saved chat
    // whose curationState wasn't persisted server-side falls back to this
    // text-detection and never recognizes an initiated build, so resumeBuild
    // never runs and a since-completed program's stale chat is left frozen
    // forever instead of resumeBuild's own contentDone check catching up.
    state.buildTriggered = chat.some(m => {
        const t = m.text || '';
        return t.startsWith('Step 1:') || t.startsWith('Step 2:')
            || t.startsWith('Step 3:') || t.startsWith('Did you know')
            || t.includes('Creating your program') || t.includes('create your program');
    });
    // Reconstruct from wizard (always available even when server strips markers)
    if (wizard?.Competencies?.length) {
        state.capturedCompetencies = {
            Competencies: wizard.Competencies.map(c => typeof c === 'string' ? { code: c } : c),
            Theme: wizard.Theme || '',
        };
    }
    if (wizard?.DetectedFrameworks?.length) {
        state.capturedFrameworks = wizard.DetectedFrameworks;
    }
    if (wizard?.Age) {
        state.capturedAge = wizard.Age;
    }
    if (wizard?.Title) {
        state.capturedTitle = wizard.Title;
    }
    if (wizard?.SessionDuration && wizard?.NumSessions) {
        state.capturedSessionInfo = {
            session_duration_minutes: wizard.SessionDuration,
            number_of_sessions: wizard.NumSessions,
        };
        state.sessionInfoCaptured = true;
    }
    return state;
};

